'use client';

import { Grid, VStack } from '@terra/ui-styled-system';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { useTranslations } from 'next-intl';
import type { GuardrailPipeline } from '@terra/shared-contracts-applications';
import {
  GuardrailActionSchema,
  GuardrailCategorySchema,
  GuardrailPipelineSchema,
} from '@terra/shared-schemas-orchestrator';
import { Field } from '../../components/field';
import { Input } from '../../components/input';
import { RadioButtonGroup } from '../../components/radio-button-group';
import { createSelectListCollection, Select } from '../../components/select';
import { Textarea } from '../../components/textarea';
import { PipelineMultiSelect } from '../testing-instructions/PipelineMultiSelect';
import { GuardrailRulesField } from './GuardrailRulesField';
import {
  nextGuardrailPipelines,
  toSelectedGuardrailPipelines,
  withoutGuardrailRuleError,
  type GuardrailFormErrors,
  type GuardrailFormValues,
} from './guardrail-form.model';

type GuardrailFormFieldsProps = {
  values: GuardrailFormValues;
  errors: GuardrailFormErrors;
  availablePipelines: readonly GuardrailPipeline[];
  setValues: Dispatch<SetStateAction<GuardrailFormValues>>;
  setErrors: Dispatch<SetStateAction<GuardrailFormErrors>>;
};

const ACTION_OPTIONS = [
  { value: 'BLOCK', labelKey: 'actions.block' },
  { value: 'WARN', labelKey: 'actions.warn' },
  { value: 'LOG', labelKey: 'actions.log' },
] as const;

export const GuardrailFormFields = ({
  values,
  errors,
  availablePipelines,
  setValues,
  setErrors,
}: GuardrailFormFieldsProps) => {
  const t = useTranslations('designSystem.guardrails');
  const categoryOptions = useMemo(
    () =>
      createSelectListCollection({
        items: GuardrailCategorySchema.options.map((category) => ({
          label: t(`categories.${category}`),
          value: category,
        })),
      }),
    [t],
  );
  const pipelineOptions = useMemo(
    () =>
      availablePipelines.map((pipeline) => ({
        label: t(`pipelines.${pipeline}`),
        value: pipeline,
      })),
    [availablePipelines, t],
  );
  const selectedPipelines = toSelectedGuardrailPipelines({
    pipelines: values.pipelines,
    available: availablePipelines,
  });
  const selectedCategoryLabel = categoryOptions.items.find(
    (item) => item.value === values.category,
  )?.label;

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      <Grid columns={{ base: 1, md: 2 }} gap={4} w="full">
        <Field.Root invalid={Boolean(errors.name)}>
          <Field.Label>{t('form.name')}</Field.Label>
          <Input
            value={values.name}
            onChange={(event) => {
              const name = event.target.value;

              setValues((previous) => ({ ...previous, name }));
              setErrors((previous) => ({ ...previous, name: undefined }));
            }}
            placeholder={t('form.namePlaceholder')}
            size="sm"
          />
          {errors.name ? (
            <Field.ErrorText>{errors.name}</Field.ErrorText>
          ) : null}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('form.category')}</Field.Label>
          <Select.Root
            positioning={{ sameWidth: true }}
            size="sm"
            collection={categoryOptions}
            value={[values.category]}
            onValueChange={(details) => {
              const parsed = GuardrailCategorySchema.safeParse(
                details.value[0],
              );

              if (parsed.success) {
                setValues((previous) => ({
                  ...previous,
                  category: parsed.data,
                }));
              }
            }}
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder={t('form.category')}>
                  {selectedCategoryLabel}
                </Select.ValueText>
                <ArrowUpDownIcon size={16} aria-hidden="true" />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup>
                  {categoryOptions.items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon aria-hidden="true" />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Field.Root>

        <Field.Root gridColumn="1 / -1">
          <Field.Label>{t('form.action')}</Field.Label>
          <RadioButtonGroup.Root
            value={values.action}
            onValueChange={(details) => {
              const parsed = GuardrailActionSchema.safeParse(details.value);

              if (parsed.success) {
                setValues((previous) => ({ ...previous, action: parsed.data }));
              }
            }}
          >
            {ACTION_OPTIONS.map((option) => (
              <RadioButtonGroup.Item key={option.value} value={option.value}>
                <RadioButtonGroup.ItemControl />
                <RadioButtonGroup.ItemText>
                  {t(option.labelKey)}
                </RadioButtonGroup.ItemText>
                <RadioButtonGroup.ItemHiddenInput />
              </RadioButtonGroup.Item>
            ))}
          </RadioButtonGroup.Root>
        </Field.Root>

        {availablePipelines.length > 0 ? (
          <Field.Root gridColumn="1 / -1">
            <PipelineMultiSelect
              label={t('form.pipelines')}
              helperText={t('form.pipelinesHelper')}
              summary={
                selectedPipelines.length === availablePipelines.length
                  ? t('pipelines.all')
                  : selectedPipelines
                      .map((pipeline) => t(`pipelines.${pipeline}`))
                      .join(', ')
              }
              options={pipelineOptions}
              value={selectedPipelines}
              onChange={(next) =>
                setValues((previous) => ({
                  ...previous,
                  pipelines: nextGuardrailPipelines({
                    current: selectedPipelines,
                    next,
                    available: availablePipelines,
                  }),
                }))
              }
              parseValue={(value) => {
                const parsed = GuardrailPipelineSchema.safeParse(value);

                return parsed.success ? parsed.data : null;
              }}
            />
          </Field.Root>
        ) : null}
      </Grid>

      <Field.Root w="full" invalid={Boolean(errors.description)}>
        <Field.Label>{t('form.description')}</Field.Label>
        <Textarea
          value={values.description ?? ''}
          onChange={(event) => {
            const description = event.target.value;

            setValues((previous) => ({ ...previous, description }));
            setErrors((previous) => ({ ...previous, description: undefined }));
          }}
          placeholder={t('form.descriptionPlaceholder')}
          rows={3}
          size="sm"
        />
        {errors.description ? (
          <Field.ErrorText>{errors.description}</Field.ErrorText>
        ) : null}
      </Field.Root>

      <GuardrailRulesField
        rules={values.rules}
        errors={errors.rules}
        onRulesChange={(rules) =>
          setValues((previous) => ({ ...previous, rules }))
        }
        onRuleEdited={(index) =>
          setErrors((previous) =>
            withoutGuardrailRuleError({ errors: previous, index }),
          )
        }
      />
    </VStack>
  );
};
